import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import ReactHtmlParser from 'react-html-parser';
import SMCLabel from '../../../components/Simple/SMCLabel';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../../components/Simple/SMCButton';
import Api from '../../../api/SMPFetchHelper';
import SMCTable from '../../../components/core/SMCTable/SMCTable';
import {ObjectTypes} from '../../../api/consts';
import {dateToString, removeItemFromArray, addItemToArray} from '../../../utils/utils';
import {SMCCheckbox} from '../../../components/Simple/SMCCheckbox';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

// TODO: Improve messages here and error handling (not alerts, show a message on body)
function FirmwareWizardGeneralAvailability({
    onCancel,
    onFinish,
    setWizardStep,
}) {
    const { t } = useTranslation();
    
    const [isDcFirmwareListLoaded, setIsDcFirmwareListLoaded] = useState(false);
    const [isDbFirmwareListLoaded, setIsDbFirmwareListLoaded] = useState(false);
    const [isAddingFirmware, setIsAddingFirmware] = useState(false);

    const isDataFullyLoaded = isDcFirmwareListLoaded && isDbFirmwareListLoaded;

    const [hasError, setHasError] = useState(false);
    const [isDone, setIsDone] = useState(false);

    const [existingFirmwares, setExistingFirmwares] = useState([]);
    const [data, setData] = useState();

    const [selectedFirmware, setSelectedFirmware] = useState([]);

    const CheckboxCell = useCallback(({object}) => {
        if (!existingFirmwares) {
            return null;
        }
        // If current firmware exists already in SMP, return 'exists', unselectable checkbox
        if (existingFirmwares.find(existingFirm => object.version === existingFirm.uid)) {
            return <SMCIcon icon="greenCheck" />
        }
        return (
            <SMCCheckbox
                inline
                checked={selectedFirmware.includes(object.version)}
                onChange={e => setSelectedFirmware(e.target.checked ? addItemToArray(selectedFirmware, object.version) : removeItemFromArray(selectedFirmware, object.version))}
            />
        )
    }, [existingFirmwares, selectedFirmware]);

    const firmwareTableColumns = useMemo(() => [
        { id: 'checkbox',     header: '',                                                      width: 32,  align: 'center', Cell: CheckboxCell                                       },
        { id: 'name',         header: t('SMP.NAME'),              accessor: 'firmwareName',    width: 100                                                                            },
        { id: 'version',      header: t('SMC_FIRM_VERSION'),      accessor: 'version',         width: 145                                                                            },
        { id: 'release_date', header: t('SMC_FIRM_RELEASE_DATE'), accessor: 'releaseDate',     width: 110,                  Cell: ({value}) => <span>{dateToString(value)}</span>    },
        { id: 'description',  header: t('SMC_FIRM_DESCRIPTION'),  accessor: 'fileDescription', width: 150                                                                            },
        { id: 'whats_new',    header: t('SMC_FIRM_WHATS_NEW'),    accessor: 'fileWhatsNew',    width: 230,                  Cell: ({value}) => <span>{ReactHtmlParser(value)}</span> }
    ], [CheckboxCell, t]);

    const doneButtonFunction = () => {
        if (isDone) {
            onFinish();
            return;
        }
        if (!selectedFirmware.length) {
            alert('No firmwares chosen');
        }
        setIsAddingFirmware(true);
        Api.createFirmwares(selectedFirmware)
            .then(() => {
                setIsDone(true);
                setIsAddingFirmware(false);
            })
            .catch(e => {
                alert(e);
                setHasError(true);
            });
    }

    const backButtonFunction = () => {
        setWizardStep('CHOOSE');
    }

    useEffect(() => {
        Api.getDcFirmwareList()
            .then(resp => {
                setData(resp.data.data);
                setIsDcFirmwareListLoaded(true);
            })
            .catch(e => {
                setHasError(true);
                alert(e);
            });
        Api.fetchObjectList(ObjectTypes.FIRMWARE)
            .then(resp => {
                setExistingFirmwares(resp.data.data);
                setIsDbFirmwareListLoaded(true);
            })
            .catch(e => {
                setHasError(true);
                alert(e);
            });
    }, []);

    return (
        <>
        <SMCModalBody>
            {
                (() => {
                    if (hasError) {
                        return <SMCLabel>Error adding firmwares, please try again</SMCLabel>;
                    }
                    if (!isDataFullyLoaded) {
                        return <SMCLabel bold fontSize="16px">{t('SMC_FIRMWIZ_DOWNLOAD_HEAD')}...</SMCLabel>;
                    }
                    if (isAddingFirmware) {
                        return <SMCLabel bold>Adding firmwares...</SMCLabel>
                    }
                    if (isDone) {
                        return <SMCLabel>Succesfuly added firmwares</SMCLabel>;
                    }
                    return (
                        <>
                        {/* Firmware V2V3 */}
                        {
                            data && data['firmwareV2V3'] && (
                                <>
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_V2V3')}</SMCLabel>
                                <SMCTable
                                    columns={firmwareTableColumns}
                                    content={data['firmwareV2V3']}
                                    rowIdSelector={obj => obj.firmwareName}
                                    height={480}
                                    width={800}
                                    hasCheckboxColumn={false}
                                />
                                </>
                            )
                        }
                        {
                            data && !data['firmwareV2V3'] && (
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_NONE_V2V3')}</SMCLabel>
                            )
                        }
                        {/* Firmware V0V1 */}
                        {
                            data && data['firmwareV0V1'] && (
                                <>
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_V0V1')}</SMCLabel>
                                <SMCTable
                                    columns={firmwareTableColumns}
                                    content={data['firmwareV0V1']}
                                    rowIdSelector={obj => obj.firmwareName}
                                    height={480}
                                    width={800}
                                    hasCheckboxColumn={false}
                                />
                                </>
                            )
                        }
                        {
                            data && !data['firmwareV0V1'] && (
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_NONE_V0V1')}</SMCLabel>
                            )
                        }
                        {/* Firmware S1S2 */}
                        {
                            data && data['firmwareS1S2'] && (
                                <>
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_S1S2')}</SMCLabel>
                                <SMCTable
                                    columns={firmwareTableColumns}
                                    content={data['firmwareS1S2']}
                                    rowIdSelector={obj => obj.firmwareName}
                                    height={480}
                                    width={800}
                                    hasCheckboxColumn={false}
                                />
                                </>
                            )
                        }
                        {
                            data && !data['firmwareS1S2'] && (
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_NONE_S1S2')}</SMCLabel>
                            )
                        }
                        {/* Firmware 1200 */}
                        {
                            data && data['firmware1200'] && (
                                <>
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_1200')}</SMCLabel>
                                <SMCTable
                                    columns={firmwareTableColumns}
                                    content={data['firmware1200']}
                                    rowIdSelector={obj => obj.firmwareName}
                                    height={480}
                                    width={800}
                                    hasCheckboxColumn={false}
                                />
                                </>
                            )
                        }
                        {
                            data && !data['firmware1200'] && (
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_NONE_1200')}</SMCLabel>
                            )
                        }
                        {/* Firmware 600 */}
                        {
                            data && data['firmware600'] && (
                                <>
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT')}</SMCLabel>
                                <SMCTable
                                    columns={firmwareTableColumns}
                                    content={data['firmware600']}
                                    rowIdSelector={obj => obj.version}
                                    height={480}
                                    width={800}
                                    hasCheckboxColumn={false}
                                />
                                </>
                            )
                        }
                        {
                            data && !data['firmware600'] && (
                                <SMCLabel>{t('SMC_DC_GET_FIRMWARE_TEXT_NONE')}</SMCLabel>
                            )
                        }
                        </>
                    )
                })()
            }
        </SMCModalBody>
        <SMCModalButtonContainer>
            <SMCButton onClick={backButtonFunction} style={{ marginRight: 5 }}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="arrow-circle-left" />
                {t('SMC_GEN_WIZBACK')}
            </SMCButton>
            <SMCButton onClick={doneButtonFunction} disabled={!isDataFullyLoaded || isAddingFirmware} style={{ marginRight: 5 }}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                {t('SMC_GEN_DONE')}
            </SMCButton>
            <SMCButton onClick={onCancel}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                {t('SMC_GEN_CANCEL')}
            </SMCButton>
        </SMCModalButtonContainer>
        </>
    );
}

export default FirmwareWizardGeneralAvailability;